/*
  Returns true if `number` looks like a phone number that can plausibly be dialed.
  Accepts any common layout (dots, dashes, spaces, parens, leading country code with or
  without '+') within the E.164 digit range. Rejects alphabetic garbage and obviously
  malformed input like doubled punctuation or a stray '+'.

  The non-international form requires 10–12 digits: 10 for an NA local number, plus 1–2
  for country-code variants. This is stricter than the ad-hoc regexes this replaced —
  which accepted 7-digit local numbers — but matches what Twilio can actually dial:
  packages/@paintscout/util/src/sms/send/send.ts prepends '+1' when < 11 digits, and
  Twilio rejects any E.164 string without a full 10-digit subscriber number.

  The international form allows 6–15 digits to accommodate short codes like AU "13 00 00".

  Real phone-number normalization happens in sms/send/send.ts before handing to Twilio —
  this validator is a UX-level sanity check.
*/
export function isPhoneNumberValid(number?: string, international = false): boolean {
  if (!number) return false

  // Only phone-appropriate characters: digits, whitespace, and common separators
  if (!/^[\d\s().+-]+$/.test(number)) return false

  // Disallow doubled punctuation like "((", "))", "..", "--" — these signal malformed input
  if (/([.()+-])\1/.test(number)) return false

  // At most one "+", and it must be at the start (after optional whitespace)
  const plusCount = (number.match(/\+/g) ?? []).length
  if (plusCount > 1) return false
  if (plusCount === 1 && !/^\s*\+/.test(number)) return false

  const digits = number.replaceAll(/\D/g, '')
  if (international) {
    // E.164 permits up to 15 digits; short codes (e.g. AU "13 00 00") can be as few as 6
    return digits.length >= 6 && digits.length <= 15
  }
  // NA numbers are 10 digits; allow up to 12 for country-code variants like "+31636363634"
  return digits.length >= 10 && digits.length <= 12
}
